import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy } from "react";

import { useViewerHasPermission } from "@/auth/shared";
import { subtitle, title } from "@/components/base/primitives";
import { RichTextDisplay } from "@/components/base/rich-text-editor/display";
import { getBlogsQueryOptions } from "@/functions/blogs/get-blogs";
import { DefaultLayout } from "@/layouts/default";

const CreateBlogForm = lazy(async () => {
  const mod = await import("@/components/blogs/create-blog-form");
  return { default: mod.CreateBlogForm };
});

const DeleteBlogForm = lazy(async () => {
  const mod = await import("@/components/blogs/delete-blog-form");
  return { default: mod.DeleteBlogForm };
});

const ReorderBlogsForm = lazy(async () => {
  const mod = await import("@/components/blogs/reorder-blog-form");
  return { default: mod.ReorderBlogsForm };
});

const UpdateBlogForm = lazy(async () => {
  const mod = await import("@/components/blogs/update-blog-form");
  return { default: mod.UpdateBlogForm };
});

export const Route = createFileRoute("/info/cedars")({
  ssr: "data-only",
  loader: ({ context: { queryClient } }) => {
    return queryClient.ensureQueryData(getBlogsQueryOptions("cedars"));
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { data: blogs } = useSuspenseQuery(getBlogsQueryOptions("cedars"));

  const canCreate = useViewerHasPermission({
    blogs: ["create"],
  });

  const canUpdate = useViewerHasPermission({
    blogs: ["update"],
  });

  return (
    <DefaultLayout
      classNames={{
        content: "py-12 px-2 max-w-2xl mx-auto flex flex-col space-y-12",
      }}
    >
      <div className="flex flex-col space-y-6 text-center">
        <div className="flex items-center justify-center gap-2">
          <h1 className={title()}>Injury Prevention and Wellness</h1>
        </div>
        <p className={subtitle()}>Sponsored Content From</p>
        <a
          className="mx-auto"
          href="https://www.cedars-sinai.org/programs/ortho/specialties/sports-medicine.html"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src="/logos/cedars-sinai.svg"
            className="mx-auto w-[270px]"
            alt="Cedars-Sinai"
          />
        </a>
      </div>
      <div className="bg-sand mx-auto flex max-w-[500px] flex-col items-center justify-center">
        <div className="flex flex-row gap-2 self-end px-6">
          {canUpdate && <ReorderBlogsForm tag="cedars" />}
          {canCreate && <CreateBlogForm tag="cedars" />}
        </div>
        <ul>
          {blogs?.map(({ id, title, summary, imageSource, link }) => (
            <li
              key={id}
              className="group relative mx-6 my-8 overflow-hidden rounded-xl bg-white"
            >
              <a href={link} target="_blank" rel="noreferrer">
                {imageSource && (
                  <img src={imageSource} className="w-full" alt={title} />
                )}
                <div className="bg-navbar-background text-navbar-foreground p-2 pl-3 text-left text-lg font-semibold group-hover:underline sm:text-xl">
                  <p>{title}</p>
                </div>
                <div className="contents-center mx-4 my-4 text-neutral-700">
                  <RichTextDisplay
                    name="faq-answer"
                    content={
                      typeof summary === "string"
                        ? JSON.parse(summary)
                        : summary
                    }
                  />
                  <p className="underline">Read More</p>
                </div>
              </a>
              {canUpdate && (
                <div className="absolute top-3 right-3 flex justify-end gap-2 rounded-md bg-white/50 px-2 py-1">
                  <UpdateBlogForm id={id} tag="cedars" />
                  <DeleteBlogForm id={id} blogTitle={title} tag="cedars" />
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </DefaultLayout>
  );
}
